import gql from 'graphql-tag';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import { groupBy, uniq } from 'lodash';
import { useCallback } from 'react';
import { sumByKey } from 'utils/collectionUtils';
import { getSafeNumber } from 'utils/numberUtils';
import useGetProgressBudget, { TUseProgressBudgetData } from './useGetProgressBudget';
import useGetProgressBudgetActuals, { TUseProgressBudgetActualsData } from './useGetProgressBudgetActuals';

export interface IProgressBudgetActuals {
  budget: number;
  actual: number;
  quantityBudget: number;
  quantityActual: number;
}

interface ProjectAncestorsNode {
  id: string;
  cursor: string;
  ancestors: Array<{ id: string }> | null;
}

interface ProjectAncestorsQuery {
  projects: ProjectAncestorsNode[];
}

type ProjectAncestorsQueryVariables = {
  filter: { id: { contains: string[] } };
  first?: number;
  after?: string | null;
};

const PROJECT_ANCESTORS_QUERY = gql`
  query BudgetProgressProjectAncestors($filter: ProjectFilter, $first: Int, $after: String) {
    projects(filter: $filter, first: $first, after: $after) {
      id
      cursor
      ancestors {
        id
      }
    }
  }
`;

export function useGetRolledUpProgressByProject() {
  const getAllProgressBudgets = useGetProgressBudget();
  const getAllProgressActuals = useGetProgressBudgetActuals();
  const paging = useGraphQLPaging();

  return useCallback(
    async (rowProjectIds: string[]): Promise<Map<string, IProgressBudgetActuals>> => {
      if (!rowProjectIds || rowProjectIds.length === 0) {
        return new Map();
      }

      const [budgets, actuals] = await Promise.all([
        getAllProgressBudgets(rowProjectIds, [], true, null),
        getAllProgressActuals(rowProjectIds, [], true, null),
      ]);

      const rowIdSet = new Set(rowProjectIds);
      const dataProjectIds = uniq(
        [...budgets.map((b) => b.projectId), ...actuals.map((a) => a.projectId)].filter((id): id is string => !!id)
      );
      const nonRowIds = dataProjectIds.filter((id) => !rowIdSet.has(id));

      const ancestorsById = new Map<string, string[]>();
      if (nonRowIds.length > 0) {
        const projectsWithAncestors = await paging<ProjectAncestorsQuery, ProjectAncestorsQueryVariables>(
          {
            document: PROJECT_ANCESTORS_QUERY,
            variables: { filter: { id: { contains: nonRowIds } } },
          },
          'projects',
          500
        );
        projectsWithAncestors.forEach((project) => {
          ancestorsById.set(
            project.id,
            (project.ancestors ?? []).map((ancestor) => ancestor.id)
          );
        });
      }

      return rollUpProgressByRow(rowProjectIds, budgets, actuals, ancestorsById);
    },
    [getAllProgressBudgets, getAllProgressActuals, paging]
  );
}

export function rollUpProgressByRow(
  rowProjectIds: string[],
  budgets: TUseProgressBudgetData[],
  actuals: TUseProgressBudgetActualsData[],
  ancestorsById: Map<string, string[]>
): Map<string, IProgressBudgetActuals> {
  const rowIdSet = new Set(rowProjectIds);

  const getOwningRowId = (projectId: string | null | undefined): string | null => {
    if (!projectId) {
      return null;
    }
    if (rowIdSet.has(projectId)) {
      return projectId;
    }
    return ancestorsById.get(projectId)?.find((ancestorId) => rowIdSet.has(ancestorId)) ?? null;
  };

  const budgetsByRow = groupBy(budgets, (budget) => getOwningRowId(budget.projectId) ?? '');
  const actualsByRow = groupBy(actuals, (actual) => getOwningRowId(actual.projectId) ?? '');

  const result = new Map<string, IProgressBudgetActuals>();
  rowProjectIds.forEach((rowId) => {
    result.set(rowId, aggregateProgressBudgetsToActuals(budgetsByRow[rowId] ?? [], actualsByRow[rowId] ?? []));
  });

  return result;
}

export default function useGetProgressBudgetActualsData() {
  const getAllProgressBudgetData = useGetProgressBudget();
  const getProgressActualsData = useGetProgressBudgetActuals();

  async function getProgressBudgetActualsData(
    projectIds: string[],
    costCodeIds: string[],
    includeSubprojects: boolean
  ): Promise<Map<string, IProgressBudgetActuals>> {
    if (!projectIds || projectIds.length === 0) {
      return new Map();
    }

    const progressBudgetsPromise = getAllProgressBudgetData(projectIds, costCodeIds, includeSubprojects, null);
    const progressActualsPromise = getProgressActualsData(projectIds, costCodeIds, includeSubprojects, null);
    const [progressBudgets, progressActuals] = await Promise.all([progressBudgetsPromise, progressActualsPromise]);

    const budgetsByProject = groupBy(progressBudgets, 'projectId');
    const actualsByProject = groupBy(progressActuals, 'projectId');

    const resultMap = new Map<string, IProgressBudgetActuals>();

    for (const projectId of projectIds) {
      const pBudgets = budgetsByProject[projectId] || [];
      const pActuals = actualsByProject[projectId] || [];
      resultMap.set(
        projectId,
        aggregateProgressBudgetsToActuals(
          pBudgets as TUseProgressBudgetData[],
          pActuals as TUseProgressBudgetActualsData[]
        )
      );
    }

    return resultMap;
  }

  return getProgressBudgetActualsData;
}

export function aggregateProgressBudgetsToActuals(
  budgets: TUseProgressBudgetData[],
  progressActuals: TUseProgressBudgetActualsData[]
): IProgressBudgetActuals {
  let budgeted = 0;
  let actual = 0;
  let budgetedQuantity = 0;

  const quantityActual = sumByKey(progressActuals, 'amount') ?? 0;
  const groupedByProjectIdCostCodeId = groupBy(
    progressActuals,
    ({ projectId, costCodeId }) => `${projectId}:${costCodeId}`
  );

  budgets.forEach((budget) => {
    const actuals = groupedByProjectIdCostCodeId[`${budget.projectId}:${budget.costCodeId}`];
    const quantity = sumByKey(actuals, 'amount');

    const budgetValue = getSafeNumber(budget.value);
    const budgetQuantity = getSafeNumber(budget.quantity);

    budgeted += budgetValue;

    const progressActual = getProgressActual({ budgetValue, budgetQuantity, quantity });
    actual += progressActual;

    budgetedQuantity += budgetQuantity;
  });

  return {
    budget: budgeted,
    actual,
    quantityBudget: budgetedQuantity,
    quantityActual,
  } as IProgressBudgetActuals;
}

export function getProgressActual({
  budgetValue,
  budgetQuantity,
  quantity,
}: {
  budgetValue: number;
  budgetQuantity: number;
  quantity: number;
}) {
  const progressRate = budgetQuantity == 0.0 ? 0.0 : budgetValue / budgetQuantity;
  return quantity * progressRate;
}
